/*
  Defines the application's navigation structure: top-level modules (shown in the
  main rail) and their optional sub-rail sections. This model is consumed by the
  shell layout components to render navigation and by the nav store to resolve the
  active module from the current route.
*/

import * as Icons from 'lucide-vue-next';
import type { Component } from 'vue';

import { ModuleId } from '@/nav/moduleEntities';

// A top-level navigation module displayed in the main rail sidebar.
export interface NavModule {
  id: ModuleId;
  label: string;
  icon: Component;
  muted?: boolean;
  footer?: boolean;
  // Not offered in the sidebar yet; its route redirects to Home until the module is built.
  hidden?: boolean;
  defaultRoute: string;
}

// All navigation modules in display order. The first segment of each `defaultRoute` is used for route-matching.
export const NAV_MODULES: NavModule[] = [
  {
    id: ModuleId.Home,
    label: 'Home',
    icon: Icons.House,
    defaultRoute: '/dashboard',
  },
  {
    id: ModuleId.Portfolio,
    label: 'Portfolio',
    icon: Icons.BarChart3,
    defaultRoute: '/portfolio',
  },
  {
    id: ModuleId.Investors,
    label: 'Investors',
    icon: Icons.ArrowLeftRight,
    defaultRoute: '/investors',
  },
  {
    id: ModuleId.Fundraising,
    label: 'Fundraising',
    icon: Icons.Filter,
    hidden: true,
    defaultRoute: '/fundraising',
  },
  {
    id: ModuleId.Forecasting,
    label: 'Forecasting',
    icon: Icons.TrendingUp,
    defaultRoute: '/forecasting',

  },
  {
    id: ModuleId.Reports,
    label: 'Reports',
    icon: Icons.FileText,
    defaultRoute: '/reports',
  },
  {
    id: ModuleId.Administration,
    label: 'Administration',
    icon: Icons.Settings2,
    muted: true,
    footer: true,
    defaultRoute: '/admin',
  },
];

// Operational modules (everything except home, administration and hidden modules) — used by the sidebar nav list.
export const OPERATIONAL_MODULES = NAV_MODULES.filter(
  (m) => m.id !== ModuleId.Administration && m.id !== ModuleId.Home && !m.hidden
);

// The home module — used by the sidebar to render the top entry.
export const HOME_MODULE = NAV_MODULES.find((m) => m.id === ModuleId.Home)!;

// The administration module — used by the sidebar footer.
export const ADMIN_MODULE = NAV_MODULES.find((m) => m.id === ModuleId.Administration)!;

/**
 * Resolves the active module from a firm-relative path.
 * Matches by comparing the first path segment against each module's `defaultRoute`.
 *
 * @param firmRelativePath - The current route path with the firm prefix stripped
 *   (e.g. "portfolio/companies" rather than "/firm/01ABC.../portfolio/companies").
 * @returns `moduleId`, null if no match is found (e.g. an unrecognized path).
 */
export function resolveNavFromPath(firmRelativePath: string): {
  moduleId: ModuleId | null;
} {
  if (!firmRelativePath || firmRelativePath === 'dashboard') {
    return { moduleId: ModuleId.Home };
  }

  for (const mod of NAV_MODULES) {
    if (mod.id === ModuleId.Home)
      continue;

    const modPrefix = mod.defaultRoute.replace(/^\//, '').split('/')[0];
    const matchesModule =
      firmRelativePath === modPrefix ||
      firmRelativePath.startsWith(modPrefix + '/');

    if (matchesModule) {
      return { moduleId: mod.id };
    }
  }

  return { moduleId: null };
}
